import { fetchTrafficPatterns } from './trafficPatterns';
import { getLastValidIteration, getToursDataFromIteration } from './SolutionHelpers';

export const executeFetchTrafficPatterns = async ({
  trafficRequestInProgress,
  trafficFF,
  solutionRequests,
  solutionShow,
  selectedTour,
  departureTimeOffset = 0,
  urlPOST,
  GETapiBase,
  apiKey,
  onSuccess,
  onError,
  signal,
}) => {
  if (trafficRequestInProgress.current || !trafficFF) {
    return;
  }

  trafficRequestInProgress.current = true;

  const freshTourData = getToursDataFromIteration(
    getLastValidIteration(solutionRequests[solutionShow]),
  );
  const freshSelectedTour = selectedTour ?? 0;

  await fetchTrafficPatterns({
    tourData: freshTourData,
    selectedTour: freshSelectedTour,
    departureTimeOffset,
    urlPOST,
    GETapiBase,
    apiKey,
    onSuccess,
    onError,
    signal,
  });

  // Only release the lock if this fetch wasn't aborted.
  // An aborted fetch means a newer fetch has already taken over and reset the ref itself.
  if (!signal?.aborted) {
    trafficRequestInProgress.current = false;
  }
};

export const findOriginIndex = (origins, location, tolerance = 0.0001) => {
  if (!origins || !Array.isArray(origins) || !location) {
    return -1;
  }

  return origins.findIndex(
    (origin) =>
      Math.abs(origin.lat - location.lat) < tolerance &&
      Math.abs(origin.lng - location.lng) < tolerance,
  );
};

export const findClosestTimestampIndex = (timestamps, targetTime) => {
  if (!timestamps || !Array.isArray(timestamps) || timestamps.length === 0) {
    return 0;
  }

  return timestamps.reduce(
    (acc, ts, idx) => {
      const timeDiff = Math.abs(new Date(ts).getTime() - targetTime);
      return timeDiff < acc.minDiff ? { closestIndex: idx, minDiff: timeDiff } : acc;
    },
    { closestIndex: 0, minDiff: Infinity },
  ).closestIndex;
};

export const getTimestampForOffset = (timestamps, offsetMs, baseTimeMs) => {
  if (!timestamps || !Array.isArray(timestamps) || timestamps.length === 0) {
    return null;
  }
  const base = baseTimeMs !== undefined ? baseTimeMs : new Date(timestamps[0]).getTime();
  const targetTime = base + (offsetMs || 0);
  return timestamps[findClosestTimestampIndex(timestamps, targetTime)];
};

export const calculateFlatIndex = (fromIndex, toIndex, matrixSize) => {
  return fromIndex * matrixSize + toIndex;
};

export const getSegmentColor = (congestionFactor) => {
  if (congestionFactor < 0.97) return '#0066FF'; // Blue: Better than normal
  if (congestionFactor < 1.0) return '#00CC00'; // Dark green: Normal/good
  if (congestionFactor < 1.03) return '#00FF00'; // Green: Slightly slower
  if (congestionFactor < 1.06) return '#7FFF00'; // Light green: Light congestion
  if (congestionFactor < 1.09) return '#CCFF00'; // Yellow-green
  if (congestionFactor < 1.12) return '#FFFF00'; // Yellow: Moderate
  if (congestionFactor < 1.15) return '#FFCC00'; // Yellow-orange
  if (congestionFactor < 1.2) return '#FFAA00'; // Orange: Heavy
  if (congestionFactor < 1.25) return '#FF6600'; // Dark orange
  return '#FF0000'; // Red: Severe
};

export const getTrafficDescription = (congestionFactor) => {
  if (congestionFactor < 0.97) return 'Better than normal';
  if (congestionFactor < 1.0) return 'Good traffic';
  if (congestionFactor < 1.03) return 'Normal traffic';
  if (congestionFactor < 1.06) return 'Light congestion (+3-6%)';
  if (congestionFactor < 1.09) return 'Light-moderate (+6-9%)';
  if (congestionFactor < 1.12) return 'Moderate congestion (+9-12%)';
  if (congestionFactor < 1.15) return 'Moderate-heavy (+12-15%)';
  if (congestionFactor < 1.2) return 'Heavy congestion (+15-20%)';
  if (congestionFactor < 1.25) return 'Very heavy (+20-25%)';
  return 'Severe congestion (+25%+)';
};

export const calculateDelayText = (congestion) => {
  if (congestion < 1.0) {
    const speedupPercent = ((1.0 - congestion) * 100).toFixed(1);
    return `${speedupPercent}% faster`;
  }
  if (congestion > 1.0) {
    const delayPercent = ((congestion - 1.0) * 100).toFixed(1);
    return `+${delayPercent}% slower`;
  }
  return 'Normal speed';
};

export const processTrafficSegments = ({
  trafficMatrix,
  tourData,
  selectedTour,
  trafficTimeOffset = 0,
}) => {
  if (!trafficMatrix?.factorsData?.matrix || !trafficMatrix?.origins || !tourData?.tours) {
    return { segmentData: [], minCongestion: 0.95, maxCongestion: 1.3 };
  }

  const { matrix } = trafficMatrix.factorsData;
  const { origins } = trafficMatrix;
  const { congestionFactors, timestamps } = matrix;

  if (!congestionFactors || !timestamps) {
    return { segmentData: [], minCongestion: 0.95, maxCongestion: 1.3 };
  }

  const currentTourData =
    selectedTour !== undefined ? tourData.tours[selectedTour] : tourData.tours[0];

  if (!currentTourData || !currentTourData.stops) {
    return { segmentData: [], minCongestion: 0.95, maxCongestion: 1.3 };
  }

  const segments = [];
  let min = Infinity;
  let max = -Infinity;

  // Process each segment
  for (let i = 0; i < currentTourData.stops.length - 1; i++) {
    const fromStop = currentTourData.stops[i];
    const toStop = currentTourData.stops[i + 1];

    if (fromStop.location && toStop.location && fromStop.time?.departure) {
      const fromOriginIndex = findOriginIndex(origins, fromStop.location);
      const toOriginIndex = findOriginIndex(origins, toStop.location);

      if (fromOriginIndex !== -1 && toOriginIndex !== -1) {
        // Apply offset to segment departure time
        const originalDepartureTime = new Date(fromStop.time.departure).getTime();
        const offsetDepartureTime = originalDepartureTime + trafficTimeOffset;

        // Find closest timestamp
        const closestTimestampIndex = findClosestTimestampIndex(timestamps, offsetDepartureTime);

        const flatIndex = calculateFlatIndex(fromOriginIndex, toOriginIndex, origins.length);
        const factorArray = congestionFactors[flatIndex];

        if (Array.isArray(factorArray) && factorArray[closestTimestampIndex] !== undefined) {
          const congestionFactor = factorArray[closestTimestampIndex];

          min = Math.min(min, congestionFactor);
          max = Math.max(max, congestionFactor);

          segments.push({
            from: [fromStop.location.lat, fromStop.location.lng],
            to: [toStop.location.lat, toStop.location.lng],
            congestion: congestionFactor,
            segmentIndex: i,
            timestamp: timestamps[closestTimestampIndex],
            offsetDepartureTime: new Date(offsetDepartureTime).toISOString(),
          });
        }
      }
    }
  }

  return {
    segmentData: segments,
    minCongestion: min === Infinity ? 0.95 : min,
    maxCongestion: max === -Infinity ? 1.3 : max,
  };
};
